import React, { useState, useEffect } from 'react';
import { 
  Users, CheckCircle, Shield, Zap, TrendingUp, BarChart, 
  ArrowRight, Play, Server, Clock, Globe, Layout, Smartphone,
  UserCheck, Calendar, DollarSign, Timer, BarChart3, UserCog,
  ShieldCheck, LayoutDashboard, Database, Lock, Cpu, Menu, X, Activity
} from 'lucide-react';
import logo from '../assest/logo.png';
import '../App.css';

const SoftwareBranding = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="font-[Poppins] antialiased text-slate-800 bg-white selection:bg-emerald-100 selection:text-emerald-900 overflow-x-hidden">
      {/* Header - EXACT SAME AS HOMEPAGE */}
      <header className="fixed top-0 left-0 right-0 z-[100] bg-white h-14 border-b border-slate-100 shadow-sm">
        <div className="max-w-[1240px] mx-auto h-full px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
             <div className="w-8 h-8">
               <img src={logo} alt="HRMS Software" className="w-full h-full object-contain" />
             </div>
             <span className="text-base font-extrabold tracking-tight text-slate-800">HRMS<span className="text-emerald-500 font-black">Software</span></span>
          </div>

          <nav className="hidden md:flex items-center gap-8 text-slate-500 font-semibold text-[13px]">
            <a href="/" className="hover:text-emerald-500 transition-all hover:scale-105">Home</a>
            <a href="/features" className="text-emerald-500 transition-all hover:scale-105 font-bold border-b-2 border-emerald-500">Features</a>
            <a href="/about" className="hover:text-emerald-500 transition-all hover:scale-105">About Us</a>
            <a href="/solutions" className="hover:text-emerald-600 transition-all hover:scale-105">Solutions</a>
            <a 
              href="/SecureInsights.apk" 
              download="SecureInsights.apk"
              className="px-6 py-2 rounded-[6px] bg-emerald-600 text-white font-bold hover:bg-emerald-700 transition-all flex items-center gap-2 active:scale-95 shadow-[0_10px_30px_rgba(16,185,129,0.25)]"
            >
               Download App
            </a>
          </nav>

          <button className="md:hidden p-2 text-slate-600" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="absolute top-14 left-0 right-0 bg-white border-b border-slate-100 shadow-xl z-[150] py-6 px-6 md:hidden">
            <nav className="flex flex-col gap-5 text-slate-600 font-bold text-sm">
              <a href="/" onClick={() => setIsMenuOpen(false)}>Home</a>
              <a href="/features" onClick={() => setIsMenuOpen(false)}>Features</a>
              <a href="/about" onClick={() => setIsMenuOpen(false)}>About Us</a>
              <a href="/solutions" onClick={() => setIsMenuOpen(false)}>Solutions</a>
              <a 
                href="/SecureInsights.apk" 
                download="SecureInsights.apk"
                className="w-full py-4 rounded-[6px] bg-emerald-600 text-white shadow-lg text-center"
              >
                Download App
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center pt-24 pb-16 overflow-hidden bg-[#070b14]">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-emerald-500/10 blur-[120px] rounded-full pointer-events-none opacity-40"></div>
        <div className="max-w-[1240px] mx-auto px-6 text-center space-y-10 relative z-10 w-full">
           <div className="inline-flex items-center gap-3 px-4 py-2 bg-emerald-500/10 rounded-full border border-emerald-500/30 backdrop-blur-md mx-auto">
              <Activity size={12} className="text-emerald-500 animate-pulse" />
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-emerald-400">HRMS Software HRMS v2.0</p>
           </div>
           <h1 className="text-4xl md:text-5xl lg:text-[72px] font-extrabold leading-[1.05] tracking-tighter text-white">
             The Intelligent Core of <br />
             <span className="text-emerald-500">Enterprise Productivity.</span>
           </h1>
           <p className="text-base md:text-lg text-slate-400 font-medium max-w-2xl mx-auto leading-relaxed">
             HRMS Software is a hyper-integrated HR Management System designed to handle high-density workforce logistics with absolute precision and security.
           </p>
           <div className="flex flex-col sm:flex-row justify-center gap-6 pt-4">
              <button className="px-10 py-5 bg-emerald-600 text-white rounded-[12px] font-black text-[14px] uppercase tracking-widest shadow-[0_20px_40px_rgba(16,185,129,0.25)] hover:bg-emerald-700 active:scale-95 transition-all">
                 Request Live Demo
              </button>
              <button className="px-10 py-5 bg-white/5 text-slate-300 rounded-[12px] border border-white/10 font-black text-[14px] uppercase tracking-widest hover:bg-white/10 transition-all">
                 Technical Specs
              </button>
           </div>
        </div>
      </section>

      {/* Modern Features Architecture */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-[1240px] mx-auto px-6">
           <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center mb-32">
              <div className="space-y-8">
                 <h2 className="text-[12px] font-extrabold text-emerald-500 uppercase tracking-[0.4em]">Precision Attendance</h2>
                 <h3 className="text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight tracking-tight">
                   Zero-Gap Attendance <br />
                   Verification.
                 </h3>
                 <p className="text-slate-500 font-medium leading-relaxed text-[15px]">
                   Our software uses dual-layer verification (GPS Geofencing + Visual Confirmation) to ensure every clock-in is authentic. Eliminate "buddy punching" forever.
                 </p>
                 <ul className="space-y-4">
                    {[
                      'Real-time GPS boundary validation',
                      'Device-specific fingerprinting',
                      'Historical location breadcrumbs',
                      'Instant late-reporting alerts'
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-4 font-bold text-[13px] text-slate-700">
                         <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
                           <CheckCircle size={14} strokeWidth={3} />
                         </div>
                         {item}
                      </li>
                    ))}
                 </ul>
              </div>
              <div className="grid grid-cols-2 gap-6 relative">
                 <div className="absolute -inset-10 bg-emerald-500/10 blur-[100px] rounded-full -z-0"></div>
                 <div className="space-y-6 pt-12 relative z-10">
                    <div className="aspect-square bg-slate-50 rounded-[28px] p-8 flex flex-col justify-between border border-slate-100 shadow-sm hover:shadow-xl transition-all">
                       <Cpu size={32} className="text-emerald-500" />
                       <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">AI Engine</p>
                    </div>
                    <div className="aspect-square bg-emerald-600 rounded-[28px] p-8 flex flex-col justify-between text-white shadow-xl shadow-emerald-600/20 hover:scale-105 transition-transform">
                       <Lock size={32} />
                       <p className="text-[10px] font-black uppercase tracking-widest">Vault Security</p>
                    </div>
                 </div>
                 <div className="space-y-6 relative z-10">
                    <div className="aspect-square bg-[#070b14] rounded-[28px] p-8 flex flex-col justify-between text-white shadow-xl hover:scale-105 transition-transform">
                       <Globe size={32} className="text-emerald-500" />
                       <p className="text-[10px] font-black uppercase tracking-widest">Edge Synch</p>
                    </div>
                    <div className="aspect-square bg-slate-50 rounded-[28px] p-8 flex flex-col justify-between border border-slate-100 shadow-sm hover:shadow-xl transition-all">
                       <Database size={32} className="text-blue-500" />
                       <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Tera-Storage</p>
                    </div>
                 </div>
              </div>
           </div>
        </div>
      </section>

      {/* Tech Stack Specs */}
      <section className="py-24 bg-slate-50 border-y border-slate-100">
        <div className="max-w-[1240px] mx-auto px-6">
           <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight">High-Integrity Infrastructure</h2>
              <p className="text-slate-500 font-medium">Built for the next decade of enterprise scalability.</p>
           </div>
           
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <SpecCard icon={LayoutDashboard} title="Modular UI" desc="Built with React 19 for fluid interactions." />
              <SpecCard icon={Lock} title="AES-256" desc="Military grade encryption for employee data." />
              <SpecCard icon={Zap} title="Sub-100ms" desc="Optimized database response across modules." />
              <SpecCard icon={Smartphone} title="Cross-Platform" desc="Native mobile experience for agents." />
           </div>
        </div>
      </section>

      {/* Footer - EXACT SAME AS HOMEPAGE */}
      <footer className="bg-[#070b14] py-16 border-t border-white/5">
         <div className="max-w-[1240px] mx-auto px-6 flex flex-col items-center justify-center text-center gap-10">
            <div className="flex items-center gap-3">
               <div className="w-10 h-10 rounded-xl bg-emerald-500/10 p-2 border border-emerald-500/20">
                  <img src={logo} alt="Logo" className="w-full h-full object-contain filter brightness-0 invert" />
               </div>
               <span className="text-xl font-black text-white tracking-tight uppercase">HRMS Software</span>
            </div>
            
            <p className="text-[10px] md:text-[11px] font-bold text-slate-500 uppercase tracking-[0.4em] max-w-[280px] md:max-w-none leading-relaxed">
               © 2026 HRMS Software — ALL RIGHTS RESERVED
            </p>
            
            <div className="flex items-center gap-8 md:gap-12 text-[11px] font-black text-slate-400">
               <a href="/" className="hover:text-emerald-500 transition-all uppercase tracking-widest hover:scale-110">Home</a>
               <a href="/about" className="hover:text-emerald-500 transition-all uppercase tracking-widest hover:scale-110">About Us</a>
               <a href="/features" className="hover:text-emerald-500 transition-all uppercase tracking-widest hover:scale-110 text-emerald-500">Software</a>
               <a href="/solutions" className="hover:text-emerald-500 transition-all uppercase tracking-widest hover:scale-110">Solutions</a>
            </div>
         </div>
      </footer>
    </div>
  );
};

const SpecCard = ({ icon: Icon, title, desc }) => (
  <div className="p-10 bg-white rounded-[24px] border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.02)] hover:shadow-[0_20px_50px_rgba(0,0,0,0.05)] transition-all group">
     <div className="w-14 h-14 rounded-[18px] bg-slate-50 text-emerald-500 flex items-center justify-center mb-6 shadow-sn group-hover:scale-110 transition-transform">
        <Icon size={28} />
     </div>
     <h4 className="text-lg font-extrabold text-slate-900 tracking-tight mb-2">{title}</h4>
     <p className="text-[14px] text-slate-500 font-medium leading-relaxed opacity-80">{desc}</p>
  </div>
);

export default SoftwareBranding;
